import React from "react";
import { FaArrowRight } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { homeFloatingCards, type HomeFloatingCard } from "../../content/indiaSiteContent";
import type { SupportedRegion } from "../../utils/regionRedirect";
import { cmsApi } from "../../services/cms";
import { useCMS } from "../../hooks/useCMS";

type HeroProps = {
  detectedRegion?: SupportedRegion | null;
  showRegionPopup?: boolean;
  onCloseRegionPopup?: () => void;
  onGoToIndiaSite?: () => void;
  onGoToUaeSite?: () => void;
};

type LandingPlaceCard = {
  id?: string;
  title?: string;
  name?: string;
  country?: string;
  description?: string;
  slug?: string;
  tag?: string;
  subtitle?: string;
  imageUrl?: string;
  image?: string;
  displayOrder?: number;
  isActive?: number | boolean;
  ctaUrl?: string;
};

type FloatingCard = HomeFloatingCard & {
  slug?: string;
  ctaUrl?: string;
};

const Hero: React.FC<HeroProps> = ({
  detectedRegion = null,
  showRegionPopup = false,
  onCloseRegionPopup,
  onGoToIndiaSite,
  onGoToUaeSite,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: landingPlaces } = useCMS<LandingPlaceCard[]>(() => cmsApi.getLandingPlaces('India'));
  const { data: destinations } = useCMS<{ name: string; slug: string }[]>(() => cmsApi.getDestinations('isActive=true'));
  const detectedRegionLabel =
    detectedRegion === "IN" ? "India" : detectedRegion === "AE" ? "UAE" : null;
  const floatingCardClasses = [
    "top-1/4 left-6",
    "bottom-24 left-20",
    "top-24 right-12",
    "bottom-1/4 right-6",
  ];
  const cmsFloatingCards: FloatingCard[] =
    (landingPlaces || [])
      .filter((card) => card.isActive === undefined || card.isActive === true || card.isActive === 1)
      .sort(
        (a, b) =>
          (a.displayOrder ?? Number.MAX_SAFE_INTEGER) - (b.displayOrder ?? Number.MAX_SAFE_INTEGER),
      )
      .slice(0, 4)
      .map((card, index) => {
        const fallbackCard = homeFloatingCards[index % homeFloatingCards.length];
        return {
          name: card.name || card.title || fallbackCard.name,
          tag: card.tag || card.subtitle || card.description || card.country || fallbackCard.tag,
          image: card.imageUrl || card.image || fallbackCard.image,
          slug: card.slug,
          ctaUrl: card.ctaUrl,
        };
      });

  const floatingCards: FloatingCard[] =
    cmsFloatingCards.length > 0
      ? cmsFloatingCards
      : homeFloatingCards.map((card) => ({ ...card }));

  return (
    <section
      id="hero"
      className="relative min-h-[93vh] flex items-center justify-center pt-24 pb-12 overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <video
          src="/india.mp4"
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-gradient-to-b from-brand-dark/70 via-brand-dark/40 to-brand-dark/90"></div>
      </div>

      {showRegionPopup && (
        <div className="fixed left-4 right-4 top-24 z-[60] mx-auto w-auto max-w-md rounded-3xl border border-white/15 bg-black/45 p-5 text-left shadow-2xl backdrop-blur-xl sm:left-auto sm:right-6 sm:top-28 sm:w-full">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-pink">
                {t("Regional site suggestion")}
              </p>

              <h2 className="mt-2 text-lg font-semibold text-white sm:text-xl">
                {t("Choose your regional site")}
              </h2>
            </div>

            <button
              type="button"
              onClick={onCloseRegionPopup}
              className="rounded-full border border-white/10 px-3 py-1 text-xs font-medium text-gray-300 transition-colors hover:border-white/30 hover:text-white"
            >
              {t("Close")}
            </button>
          </div>

          <p className="mt-3 text-sm leading-6 text-gray-300">
            {detectedRegionLabel
              ? `We detected you're visiting from ${detectedRegionLabel}. Choose the website you want to continue with.`
              : t("Please choose the website you want to continue with.")}
          </p>

          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={onGoToIndiaSite}
              className={`rounded-full px-5 py-3 text-sm font-medium text-white transition-colors ${
                detectedRegion === "IN"
                  ? "bg-brand-pink hover:bg-brand-pink/90"
                  : "border border-white/15 hover:border-white/30 hover:bg-white/5"
              }`}
            >
              {t("India")}
            </button>

            <button
              type="button"
              onClick={onGoToUaeSite}
              className={`group flex items-center justify-center gap-3 rounded-full px-5 py-3 text-sm font-semibold text-white transition-colors ${
                detectedRegion === "AE"
                  ? "bg-brand-pink hover:bg-brand-pink/90"
                  : "border border-white/15 hover:border-white/30 hover:bg-white/5"
              }`}
            >
              <span>{t("UAE")}</span>
              <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      )}

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center text-center">
        <span className="text-brand-pink text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-4 animate-slide-up">
          {t("Crafted for India")}
        </span>

        <h2 className="font-heading text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6 max-w-4xl animate-slide-up">
          {t("Worldwide Holidays")} <br />
          <span className="italic text-brand-pink">{t("for Indian")}</span>{" "}
          {t("Travellers")}
        </h2>

        <p className="text-gray-200 text-base sm:text-lg md:text-xl max-w-2xl mb-12 font-light animate-slide-up">
          {t(
            "Explore handpicked international holidays, destination ideas, and visa assistance designed around the way Indian travellers plan.",
          )}
        </p>

        <div className="flex flex-col sm:flex-row gap-4 animate-slide-up">
          <Link
            to="/destination"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-pink px-8 py-4 text-white shadow-lg shadow-brand-pink/20 transition-colors hover:bg-brand-pink/90"
          >
            <span>{t("Explore Destinations")}</span>
            <FaArrowRight className="text-sm" />
          </Link>
          <Link
            to="/visa-services"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-8 py-4 text-white transition-colors hover:bg-white/10"
          >
            <span>{t("Check Visa Services")}</span>
          </Link>
        </div>

        {floatingCards.map((card, index) => (
          <div
            key={card.name}
            className={`absolute hidden xl:block ${floatingCardClasses[index]} animate-float cursor-pointer`}
            style={{ animationDelay: `${index * 0.35}s` }}
            onClick={() => {
              const destination = destinations?.find((d) => d.name === card.name);
              navigate(card.ctaUrl || `/destination-detail/${destination?.slug || card.name.toLowerCase().replace(/\s+/g, '-')}`);
            }}
          >
            <div className="glass-dark flex items-center gap-3 rounded-2xl p-3 shadow-2xl hover:scale-105 transition-transform">
              <img
                src={card.image}
                alt={card.name}
                className="h-12 w-12 rounded-xl object-cover"
              />

              <div className="text-left">
                <p className="text-sm font-medium text-white">{t(card.name)}</p>
                <p className="text-xs text-brand-pink">{t(card.tag)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Hero;
